import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppRoutes } from './routes/AppRoutes';
import { ScrollToTop } from './components/ScrollToTop';
import { useAuthStore } from './store/auth.store';
import { authService } from './services/auth.service';

const App = () => {
  const queryClient = useQueryClient();
  const hydrate = useAuthStore((s) => s.hydrate);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Rafraîchit le profil (roles/kycStatus) au montage et à chaque retour sur l'onglet :
  // l'accès « espace créateur » apparaît dès que l'admin a validé le KYC, sans
  // reconnexion. Best-effort — un échec laisse le profil en cache.
  useEffect(() => {
    const refreshProfile = () => {
      if (!useAuthStore.getState().accessToken) return;
      authService
        .me()
        .then(setUser)
        .catch(() => {
          /* réseau/401 gérés ailleurs */
        });
    };
    refreshProfile();
    window.addEventListener('focus', refreshProfile);
    return () => window.removeEventListener('focus', refreshProfile);
  }, [setUser]);

  // Expiration de session (émise par l'intercepteur axios après échec du refresh) :
  // on redirige vers /login avec retour prévu, pour ne jamais laisser une page
  // « connectée » dans un état cassé.
  useEffect(() => {
    const onSessionExpired = () => {
      queryClient.clear();
      if (location.pathname !== '/login') {
        toast.error('Session expirée. Reconnecte-toi pour continuer.');
        navigate('/login', {
          replace: true,
          state: { from: location.pathname + location.search },
        });
      }
    };
    window.addEventListener('tickart:session-expired', onSessionExpired);
    return () => window.removeEventListener('tickart:session-expired', onSessionExpired);
  }, [navigate, location, queryClient]);

  return (
    <>
      <ScrollToTop />
      <AppRoutes />
    </>
  );
};

export default App;
